import { filter, head, isNil, map, pipe, take, toArray } from '@fxts/core';
import { Enable, html, on, rune, View } from 'rune-ts';
import { typo } from '../../../../shared/typography/typo';
import { InputCheckIcon } from '../Icon';
import klass from './Input.module.scss';
import { InputBase } from './InputBase';
import {
  InputProps,
  NumberErrorHelper,
  NumberErrorHelperState,
  InputNumberProps,
  TextErrorHelper,
  TextErrorHelperState,
  InputTextProps,
} from './type';
import { InputChangedEvent, InputResetAfterEvent } from './InputEvent';
import { debounce } from '../../../../shared/util';

type HelperProps = InputTextProps | InputNumberProps;

function checkErrorType(error_state: TextErrorHelperState | NumberErrorHelperState, value: string | number) {
  if (error_state.type === 'string' && typeof value === 'string') {
    return error_state.isError(value);
  }

  if (error_state.type === 'number' && typeof value === 'number') {
    return error_state.isError(value);
  }

  return false;
}

function makeErrorState(error: TextErrorHelper | NumberErrorHelper, type: InputProps['type']) {
  if (type === 'number') {
    return {
      ...error,
      type: 'number',
    } as NumberErrorHelperState;
  } else {
    return {
      ...error,
      type: 'string',
    } as TextErrorHelperState;
  }
}

export class InputHelper extends View<HelperProps> {
  error?: TextErrorHelperState | NumberErrorHelperState;
  errors?: (TextErrorHelperState | NumberErrorHelperState)[];
  is_error: boolean;
  validPossible: boolean;

  constructor(data: HelperProps) {
    super(data);

    this.validPossible = false;
    this.is_error = false;
    const { error_helper } = this.data;

    if (error_helper instanceof Array) {
      this.errors = error_helper.map((error) => makeErrorState(error, this.data.type));
      this.validPossible = true;
      return;
    }

    if (error_helper?.message) {
      this.error = makeErrorState(error_helper, this.data.type);
      this.validPossible = true;
    }
  }

  override template() {
    const error_helper_mode = this.data.error_helper_mode || 'default';
    const show_error_mobile = this.data.show_error_mobile || false;

    if (this.errors && error_helper_mode === 'single') {
      const errors = pipe(
        this.errors,
        filter((error) => error.is_error),
        take(1),
        toArray,
      );

      if (errors.length >= 1 && errors[0]) {
        const error = errors[0];

        return html`
          <div class="${klass.helper}">
            <span data-error="${error.is_error}" class="${typo('12_medium')} ${klass.helper_text}"
              >${error.message}</span
            >
          </div>
        `;
      }
    }

    if (this.errors && error_helper_mode === 'default') {
      const errors = this.errors.map(
        (error) =>
          html`<div
            data-error="${isNil(error.is_error) ? null : error.is_error}"
            class="${typo('12_medium')} ${klass.helper_error}"
          >
            ${error.message}${error.is_error === false
              ? html`<div class="${klass.check}">${InputCheckIcon}</div>`
              : ''}
          </div>`,
      );

      return html` <div class="${klass.helper}">${errors}</div> `;
    }

    if ((rune.getSharedData(this)?.is_mobile ?? this.data.is_mobile) && !show_error_mobile) {
      return html`<div class="${klass.helper}"></div>`;
    }

    if (this.error && this.error.is_error) {
      return html`
        <div class="${klass.helper}">
          <span data-error="${this.error.is_error}" class="${typo('12_medium')} ${klass.helper_text}"
            >${this.error.message}</span
          >
        </div>
      `;
    }

    const { text_helper } = this.data;
    return html`
      <div class="${klass.helper}">
        ${isNil(text_helper)
          ? ''
          : html`<span class="${typo('12_medium')} ${klass.helper_text}">${text_helper}</span>`}
      </div>
    `;
  }

  setError() {
    if (!this.validPossible) return;

    const { value, type } = this.data;

    if (this.errors) {
      this.errors = pipe(
        this.errors,
        map((error) => {
          if (!value || value === '' || value === 0) {
            error.is_error = undefined;
            return error;
          }

          const test_value = value ?? (type === 'number' ? 0 : '');
          const is_error = checkErrorType(error, test_value);

          if (!isNil(is_error)) {
            error.is_error = is_error;
          }

          return error;
        }),
        toArray,
      );

      this.is_error =
        pipe(
          this.errors,
          filter((error) => error.is_error),
          toArray,
        ).length > 0;
    }

    if (this.error) {
      if (!value || value === '' || value === 0) {
        this.error.is_error = undefined;
        this.is_error = false;
        return;
      }

      const test_value = value ?? (type === 'number' ? 0 : '');
      const is_error = checkErrorType(this.error, test_value);

      if (!isNil(is_error)) {
        this.error.is_error = is_error;
        this.is_error = is_error;
      }
    }
  }

  getIsError() {
    return this.is_error;
  }

  getError() {
    const { value, type } = this.data;
    return pipe(
      this.errors ? this.errors : this.error ? [this.error] : [],
      filter((error) => {
        const test_value = value ?? (type === 'number' ? 0 : '');
        return checkErrorType(error, test_value);
      }),
      head,
    );
  }

  setErrorMessage(message: string) {
    if (this.error) {
      this.error.message = message;
    }
  }

  setIsError(is_error: boolean, message?: string) {
    if (this.error) {
      this.is_error = true;
      this.error.is_error = is_error;
      this.error.message = message ?? this.error.message;
    }
  }
}

export class InputHelperEnable<D extends InputProps, V extends InputBase<D>> extends Enable<D> {
  constructor(public override view: V) {
    super(view);
  }

  @on(InputResetAfterEvent)
  resetHandler() {
    this.view.inputHelperView.setError();
    this.view.inputHelperView.redraw();
    this.view.element().dataset.error = String(false);
  }

  @on(InputChangedEvent)
  changedHandler() {
    if (!this.view.inputHelperView.validPossible) return;
    return this.helperHandler();
  }

  helperHandler = debounce(() => {
    this.view.inputHelperView.setError();
    this.view.inputHelperView.redraw();

    const is_error = this.view.inputHelperView.getIsError();
    this.element().dataset.error = String(is_error);
  }, 50);
}
